import React, { useRef, useState, useEffect } from 'react';
import { MediaResponse } from '../../../types/api/common';
import { Stack, Button, Text } from '@primer/react';
import { FileMovedIcon, TrashIcon, FileMediaIcon } from '@primer/octicons-react';
import { useAlert } from '../../../context/alert';

interface ImageComponentProps {
    media: MediaResponse | undefined
    onFileSelect: (file: File | undefined) => void;
}

const ImageComponent = ({ media, onFileSelect }: ImageComponentProps) => {
    const [categoryImage, setCategoryImage] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    useEffect(() => {
        if (media) {
            if (media.preview) {
                setCategoryImage(media.preview);
            } else if (media.file) {
                setCategoryImage(media.file);
            }
        }
    }, [media]);

    // Валидация файла
    const validateFile = (file: File): boolean => {
        // Проверка размера (20 МБ)
        if (file.size > 20 * 1024 * 1024) {
            setError('Превышен максимальный размер файла (20 Мб)');
            return false;
        }

        // Проверка формата
        if (!['image/png', 'image/svg+xml'].includes(file.type)) {
            setError('Недопустимый формат изображения (только png, svg)');
            return false;
        }

        return true;
    };

    // Обработка выбранного файла
    const handleFileSelect = (file: File) => {
        if (validateFile(file)) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setCategoryImage(e.target?.result as string);
            };
            reader.readAsDataURL(file);
            onFileSelect(file)
        }
    };

    const selectImage = () => {
        fileInputRef.current?.click();
    };

    const changeImage = () => {
        fileInputRef.current?.click();
    };

    const deleteImage = () => {
        setCategoryImage('');
        onFileSelect(undefined)
    };

    return (
        <Stack gap='normal'>
            <input
                ref={fileInputRef}
                type="file"
                accept=".png,.svg"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                    e.target.value = '';
                }}
                className="hidden"
            />
            <Stack direction="horizontal" gap='normal' justify='start'>
                {categoryImage ? (
                    <>
                        <div
                            style={{
                                backgroundImage: `url("${categoryImage}")`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                width: "40px",
                                height: "40px",
                                minWidth: "40px",
                                minHeight: "40px",
                                borderRadius: "50%",
                                flexShrink: 0
                            }}
                        />
                        <Button
                            variant="default"
                            leadingVisual={FileMovedIcon}
                            onClick={changeImage}
                        >
                            Заменить
                        </Button>
                        <Button
                            variant="danger"
                            leadingVisual={TrashIcon}
                            onClick={deleteImage}
                        >
                            Удалить
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="default"
                        leadingVisual={FileMediaIcon}
                        onClick={selectImage}
                    >
                        Добавить изображение
                    </Button>
                )}
                <div></div>
            </Stack>
            <Text as="span" weight='normal' className='ml-[6px] text-[12px] text-aloix-grey whitespace-pre-line'>
                Максимальный размер – 20 Мб; Формат изображения – png, svg
            </Text>
        </Stack >
    );
};

export default ImageComponent;